import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import useSolutionDetails from "../hooks/useSolutionDetails";
import ErrorFallback from "../../common/ErrorFallback";
import SuccessRibbon from "../../common/SuccessRibbon";

import ErrorRibbon from "../../common/ErrorRibbon";
import { useNavigate, useParams } from "react-router";
import { SyntheticEvent, useState } from "react";
import TabSection from "../../common/TabSection";
import EarthlyWebsiteTab from "./tabs/earthly-org";
import IncidentMonitoringTab from "./tabs/incident-monitoring";
import AllFieldsTab from "./tabs/all-fields";
import { UpdateSolutionDetailsDrawer } from "../edit/page";

function tabInfo(index: number) {
  return {
    id: `solution-details-tab-${index}`,
    "aria-controls": `solution-details-tabpanel-${index}`,
  };
}

function SolutionDetails() {
  const { id: solutionId } = useParams<{ id: string }>();
  if (!solutionId) {
    throw new Error("Solution ID is required");
  }
  const navigate = useNavigate();
  const { details, error, loading, updateSolutionDetails, linkToStripe } =
    useSolutionDetails(solutionId);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (error) {
    return (
      <>
        <ErrorFallback error={error} />
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </>
    );
  }

  if (loading) {
    return (
      <Box
        data-testid="solution-details-loading-indicator"
        sx={{
          minHeight: "80vh",
          display: "flex",
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (details) {
    const { name, riskMonitoring } = details;

    return (
      <>
        <SuccessRibbon
          data={{
            details: updateSolutionDetails.alert,
            setAlert: updateSolutionDetails.setAlert,
          }}
        />
        <SuccessRibbon
          data={{
            details: linkToStripe.alert,
            setAlert: linkToStripe.setAlert,
          }}
        />
        <ErrorRibbon
          data={{
            details: linkToStripe.alert,
            setAlert: linkToStripe.setAlert,
          }}
        />
        <UpdateSolutionDetailsDrawer
          loading={updateSolutionDetails.loading}
          isEditing={updateSolutionDetails.isEditing}
          handleEdit={(value) => updateSolutionDetails.setEditing(value)}
          updateSolution={updateSolutionDetails.update}
          errorAlert={{
            details: updateSolutionDetails.alert,
            setAlert: updateSolutionDetails.setAlert,
          }}
          solutionDetails={details}
        />
        <Box
          data-testid="solution-details-page-container-outer"
          sx={{
            paddingTop: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            data-testid="solution-details-page-container-inner"
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: 1200,
              px: 2,
            }}
          >
            <Button
              data-testid="edit-solution-button"
              variant="contained"
              size="medium"
              onClick={() => {
                updateSolutionDetails.setAlert(null);
                updateSolutionDetails.setEditing(true);
              }}
              sx={{
                alignSelf: "flex-end",
                mb: 2,
              }}
            >
              Edit Project
            </Button>
            <Box data-testid="fields-container" sx={{ width: "100%" }}>
              <Typography
                id="solution-name-header"
                sx={{ paddingBottom: 1 }}
                align="left"
                variant="h4"
              >
                {name}
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="solution details tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="All Fields" {...tabInfo(0)} />
                  <Tab label="Earthly.org" {...tabInfo(1)} />
                  <Tab label="Incident Monitoring" {...tabInfo(2)} />
                </Tabs>
              </Box>

              <TabSection value={tabValue} index={0}>
                <AllFieldsTab details={details} linkToStripe={linkToStripe} />
              </TabSection>

              <TabSection value={tabValue} index={1}>
                <EarthlyWebsiteTab
                  details={details}
                  updateSolution={updateSolutionDetails.update}
                  updateLoading={updateSolutionDetails.loading}
                  updateAlert={updateSolutionDetails.alert}
                />
              </TabSection>

              <TabSection value={tabValue} index={2}>
                <Box sx={{ paddingBottom: 4 }}>
                  <IncidentMonitoringTab
                    solutionId={solutionId}
                    riskMonitoringInfo={riskMonitoring}
                  />
                </Box>
              </TabSection>
            </Box>
          </Box>
        </Box>
      </>
    );
  }
  return null;
}

export default SolutionDetails;
